import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  MailWorkflowService,
  MailTemplateDto,
  FileClassDto,
  MemoFieldDto
} from './mail-workflow.service';

type TemplateCategory = 'ALL' | 'GENERAL' | 'HR' | 'FINANCE' | 'LEGAL';

@Component({
  selector: 'app-mail-memo-template',
  template: `
    <div class="mmt-container">

      <!-- Top bar -->
      <div class="mmt-topbar">
        <button class="mmt-back-btn" (click)="goBack()">
          <span class="material-icons">arrow_back</span>
        </button>
        <div class="mmt-topbar-info">
          <span class="mmt-topbar-title">New Internal Memo</span>
          <span class="mmt-topbar-sub">Step 1 — Select a template</span>
        </div>
      </div>

      <!-- Header bar inside content -->
      <div class="mmt-page-header">
        <div>
          <div class="mmt-page-title">Select Memo Template</div>
          <div class="mmt-page-sub">
            Pick a letterhead template to open in OnlyOffice — your organisation's letterhead will be pre-populated.
          </div>
        </div>
        <div class="mmt-header-controls">
          <div class="mmt-category-tabs">
            <button *ngFor="let cat of categories"
                    class="mmt-cat-tab"
                    [class.mmt-cat-tab--active]="selectedCategory === cat.value"
                    (click)="selectedCategory = cat.value">
              {{ cat.label }}
            </button>
          </div>
          <div class="mmt-search">
            <span class="material-icons">search</span>
            <input [(ngModel)]="searchTerm" placeholder="Search templates…">
          </div>
        </div>
      </div>

      <!-- Template meta form (subject, file class, priority — needed before OO opens) -->
      <div class="mmt-meta-bar">
        <div class="mmt-meta-field">
          <label class="mmt-meta-label">Memo Subject <span class="mmt-req">*</span></label>
          <input class="mmt-meta-input" [(ngModel)]="memoSubject" placeholder="e.g. Office Closure — Easter Holiday">
        </div>
        <div class="mmt-meta-field">
          <label class="mmt-meta-label">File Class <span class="mmt-req">*</span></label>
          <select class="mmt-meta-input" [(ngModel)]="selectedFileClassId">
            <option value="">— Select —</option>
            <option *ngFor="let fc of fileClasses" [value]="fc.id">{{ fc.name }} ({{ fc.code }})</option>
          </select>
        </div>
        <div class="mmt-meta-field mmt-meta-field--sm">
          <label class="mmt-meta-label">Priority</label>
          <div class="mmt-priority-row">
            <button *ngFor="let p of priorities"
                    class="mmt-prio-btn"
                    [class.mmt-prio-btn--active]="priority === p"
                    (click)="priority = p">{{ p }}</button>
          </div>
        </div>
      </div>

      <!-- Template grid -->
      <div class="mmt-content">

        <!-- Loading -->
        <div class="mmt-loading" *ngIf="loading">
          <div class="mmt-spinner"></div>
          <span>Loading templates from Alfresco…</span>
        </div>

        <!-- Error -->
        <div class="mmt-error" *ngIf="loadError && !loading">
          <span class="material-icons">error_outline</span> {{ loadError }}
        </div>

        <!-- Grid -->
        <div class="mmt-grid" *ngIf="!loading && !loadError">

          <!-- Standard templates from backend -->
          <div class="mmt-card"
               *ngFor="let tpl of filteredTemplates"
               [class.mmt-card--selected]="selectedTemplate?.id === tpl.id"
               (click)="selectTemplate(tpl)">
            <div class="mmt-preview" [style.border-top-color]="categoryColor(tpl.category)">
              <div class="mmt-preview-header" [style.background]="categoryColor(tpl.category)">
                <div class="mmt-preview-header-line"></div>
              </div>
              <div class="mmt-preview-lines">
                <div class="mmt-pline mmt-pline--title"></div>
                <div style="height:12px"></div>
                <div class="mmt-pline"></div>
                <div class="mmt-pline"></div>
                <div class="mmt-pline mmt-pline--short"></div>
              </div>
              <div class="mmt-selected-badge" *ngIf="selectedTemplate?.id === tpl.id">
                <span class="material-icons">check</span>
              </div>
              <div class="mmt-cat-badge" [style.background]="categoryColor(tpl.category)">{{ tpl.category }}</div>
            </div>
            <div class="mmt-card-info">
              <div class="mmt-card-name">{{ tpl.name }}</div>
              <div class="mmt-card-sub">{{ tpl.description }}</div>
            </div>
          </div>

          <!-- Upload custom -->
          <div class="mmt-card mmt-card--upload" (click)="customFileInput.click()">
            <div class="mmt-preview mmt-preview--upload">
              <span class="material-icons">add_circle_outline</span>
              <div class="mmt-upload-label">Upload Custom Template</div>
              <input #customFileInput type="file" style="display:none"
                     accept=".docx"
                     (change)="onCustomTemplate($event)">
            </div>
            <div class="mmt-card-info">
              <div class="mmt-card-name">Upload Your Own</div>
              <div class="mmt-card-sub">Use a custom .docx file as your template</div>
            </div>
          </div>

          <!-- Empty state -->
          <div class="mmt-empty" *ngIf="filteredTemplates.length === 0 && !loading">
            <span class="material-icons">folder_open</span>
            <p>No templates found. Try a different category or search term.</p>
          </div>

        </div>
      </div>

      <!-- Footer actions -->
      <div class="mmt-footer">
        <button class="mmt-btn mmt-btn--ghost" (click)="goBack()">Cancel</button>
        <div style="flex:1"></div>
        <div class="mmt-selected-label" *ngIf="selectedTemplate">
          <span class="material-icons">check_circle</span>
          Selected: <strong>{{ selectedTemplate.name }}</strong>
        </div>
        <button class="mmt-btn mmt-btn--primary"
                [disabled]="!selectedTemplate || !memoSubject || !selectedFileClassId || opening || loadingFields"
                (click)="continueToFields()">
          <span class="material-icons">arrow_forward</span>
          <span *ngIf="!loadingFields && !opening">Continue</span>
          <span *ngIf="loadingFields">Loading fields…</span>
          <span *ngIf="opening">Creating…</span>
        </button>
      </div>

      <!-- Step 2 — dynamic memo fields (config-driven from the document type) -->
      <div class="mmt-modal-overlay" *ngIf="showFieldsStep" (click)="backToTemplates()">
        <div class="mmt-modal" (click)="$event.stopPropagation()">
          <div class="mmt-modal-head">
            <div>
              <div class="mmt-modal-title">Memo details</div>
              <div class="mmt-modal-sub">These fill the document and are saved as its index.</div>
            </div>
            <button class="mmt-back-btn" (click)="backToTemplates()"><span class="material-icons">close</span></button>
          </div>

          <div class="mmt-modal-body">
            <div class="mmt-field">
              <label class="mmt-meta-label">Subject</label>
              <div class="mmt-readonly">{{ memoSubject }}</div>
            </div>

            <div class="mmt-field" *ngFor="let f of memoFields">
              <label class="mmt-meta-label">
                {{ f.label || f.propertyName }}<span class="mmt-req" *ngIf="f.mandatory"> *</span>
              </label>

              <!-- person / role field → Keycloak picker (To / Through / From, single or multi) -->
              <sp-directory-picker *ngIf="isDirectoryField(f); else nonDirField"
                  [mode]="dirMode(f)"
                  [multi]="f.multiValue"
                  [restrictRoles]="f.restrictRoles || ''"
                  [placeholder]="f.placeholder || ('Search ' + (dirMode(f) === 'role' ? 'roles' : 'users') + '…')"
                  [value]="indexValues[f.propertyName] || (f.multiValue ? [] : null)"
                  (valueChange)="indexValues[f.propertyName] = $event">
              </sp-directory-picker>

              <ng-template #nonDirField>
              <!-- Feature 2/7 — multi-value field renders a repeatable +Add list (in order) -->
              <div class="mmt-multi" *ngIf="f.multiValue; else singleField">
                <div class="mmt-multi-row" *ngFor="let v of multiRows(f); let i = index; trackBy: trackByMultiRow">
                  <span class="mmt-multi-pos">{{ i + 1 }}</span>
                  <input class="mmt-meta-input" [placeholder]="f.placeholder || ''"
                         [ngModel]="multiRows(f)[i]" (ngModelChange)="setMultiRow(f, i, $event)" />
                  <button type="button" class="mmt-multi-del" title="Remove"
                          (click)="removeMultiRow(f, i)">×</button>
                </div>
                <button type="button" class="mmt-multi-add" (click)="addMultiRow(f)">
                  + Add another
                </button>
              </div>

              <ng-template #singleField>
              <ng-container [ngSwitch]="f.type">
                <select *ngSwitchCase="'select'" class="mmt-meta-input"
                        [(ngModel)]="indexValues[f.propertyName]">
                  <option value="">— Select —</option>
                  <option *ngFor="let o of f.options" [value]="o.value">{{ o.label || o.value }}</option>
                </select>

                <input *ngSwitchCase="'date'" type="date" class="mmt-meta-input"
                       [(ngModel)]="indexValues[f.propertyName]">

                <input *ngSwitchCase="'number'" type="number" class="mmt-meta-input"
                       [placeholder]="f.placeholder || ''" [(ngModel)]="indexValues[f.propertyName]">

                <textarea *ngSwitchCase="'textarea'" rows="3" class="mmt-meta-input"
                          [placeholder]="f.placeholder || ''" [(ngModel)]="indexValues[f.propertyName]"></textarea>

                <label *ngSwitchCase="'boolean'" class="mmt-check">
                  <input type="checkbox" [(ngModel)]="indexValues[f.propertyName]"> Yes
                </label>

                <input *ngSwitchDefault type="text" class="mmt-meta-input"
                       [placeholder]="f.placeholder || ''" [(ngModel)]="indexValues[f.propertyName]">
              </ng-container>
              </ng-template>
              </ng-template>
            </div>

            <div class="mmt-error" *ngIf="fieldsError">
              <span class="material-icons">error_outline</span> {{ fieldsError }}
            </div>
          </div>

          <div class="mmt-modal-foot">
            <button class="mmt-btn mmt-btn--ghost" (click)="backToTemplates()">Back</button>
            <div style="flex:1"></div>
            <button class="mmt-btn mmt-btn--primary"
                    [disabled]="!canCreateMemo() || opening"
                    (click)="openInEditor()">
              <span class="material-icons">edit</span>
              <span *ngIf="!opening">Create &amp; open in OnlyOffice</span>
              <span *ngIf="opening">Creating…</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  `,
  styles: [`
    /* Container */
    .mmt-container {
      display: flex; flex-direction: column;
      height: 100%; background: #f0f4fa; font-family: 'DM Sans', sans-serif;
    }

    /* Top bar */
    .mmt-topbar {
      background: #1B3A6B; padding: 0 20px;
      height: 52px; display: flex; align-items: center; gap: 12px;
      flex-shrink: 0;
    }
    .mmt-back-btn {
      background: rgba(255,255,255,0.1); border: none; cursor: pointer;
      border-radius: 6px; width: 32px; height: 32px;
      display: flex; align-items: center; justify-content: center; color: white;
    }
    .mmt-back-btn:hover { background: rgba(255,255,255,0.2); }
    .mmt-topbar-info {}
    .mmt-topbar-title { color: white; font-weight: 700; font-size: 14px; display: block; }
    .mmt-topbar-sub { color: rgba(255,255,255,0.5); font-size: 11px; }

    /* Page header */
    .mmt-page-header {
      background: white; border-bottom: 1px solid #e0e8f4;
      padding: 16px 24px; display: flex; align-items: flex-start;
      justify-content: space-between; gap: 16px; flex-wrap: wrap;
    }
    .mmt-page-title { font-size: 16px; font-weight: 700; color: #1B3A6B; }
    .mmt-page-sub { font-size: 12px; color: #8a9bb0; margin-top: 2px; }
    .mmt-header-controls { display: flex; align-items: center; gap: 10px; }

    /* Category tabs */
    .mmt-category-tabs { display: flex; gap: 4px; }
    .mmt-cat-tab {
      border: 1px solid #d8e4f0; border-radius: 6px;
      padding: 6px 12px; font-size: 11px; font-weight: 600;
      cursor: pointer; background: white; color: #6b7280;
      transition: all 0.12s;
    }
    .mmt-cat-tab--active { background: #1B3A6B; color: white; border-color: #1B3A6B; }

    /* Step 2 modal */
    .mmt-modal-overlay { position: fixed; inset: 0; background: rgba(15,30,60,0.45);
      display: flex; align-items: center; justify-content: center; z-index: 1000; }
    .mmt-modal { background: #fff; width: 520px; max-width: 92vw; max-height: 86vh;
      border-radius: 12px; display: flex; flex-direction: column;
      box-shadow: 0 18px 50px rgba(0,0,0,0.3); overflow: hidden; }
    .mmt-modal-head { display: flex; align-items: flex-start; justify-content: space-between;
      padding: 18px 20px; border-bottom: 1px solid #e0e8f4; }
    .mmt-modal-title { font-size: 15px; font-weight: 700; color: #1B3A6B; }
    .mmt-modal-sub { font-size: 11.5px; color: #8a9bb0; margin-top: 2px; }
    .mmt-modal-body { padding: 16px 20px; overflow-y: auto;
      display: flex; flex-direction: column; gap: 14px; }
    .mmt-modal-foot { display: flex; align-items: center; gap: 10px; padding: 14px 20px;
      border-top: 1px solid #e0e8f4; background: #f7faff; }
    .mmt-field { display: flex; flex-direction: column; gap: 5px; }
    .mmt-readonly { font-size: 13px; color: #344563; background: #f0f4fa;
      border-radius: 6px; padding: 8px 10px; }
    .mmt-check { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #344563; }

    .mmt-search {
      display: flex; align-items: center; gap: 6px;
      border: 1px solid #d8e4f0; border-radius: 6px;
      padding: 6px 12px; background: white; font-size: 12px; color: #8a9bb0;
    }
    .mmt-search .material-icons { font-size: 15px; color: #aab5c5; }
    .mmt-search input {
      border: none; outline: none; font-size: 12px;
      color: #1a1a2e; width: 160px; background: transparent;
    }

    /* Metadata bar */
    .mmt-meta-bar {
      background: #f8faff; border-bottom: 1px solid #e0e8f4;
      padding: 12px 24px; display: flex; gap: 16px; align-items: flex-end;
    }
    .mmt-meta-field { display: flex; flex-direction: column; gap: 4px; flex: 1; }
    .mmt-meta-field--sm { flex: 0 0 auto; }
    .mmt-meta-label {
      font-size: 10px; font-weight: 700; color: #8a9bb0;
      text-transform: uppercase; letter-spacing: 0.4px;
    }
    .mmt-req { color: #e53e3e; }
    .mmt-meta-input {
      border: 1px solid #d8e4f0; border-radius: 6px;
      padding: 7px 10px; font-size: 12px; color: #1a1a2e; background: white;
    }
    .mmt-meta-input:focus { outline: none; border-color: #2B5CB8; }
    /* Feature 2/7 — multi-value (+Add) rows */
    .mmt-multi { display: flex; flex-direction: column; gap: 5px; }
    .mmt-multi-row { display: flex; align-items: center; gap: 6px; }
    .mmt-multi-row .mmt-meta-input { flex: 1; }
    .mmt-multi-pos {
      width: 18px; height: 18px; flex: none; border-radius: 50%;
      background: #2B5CB8; color: #fff; font-size: 10px; font-weight: 700;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .mmt-multi-del {
      border: none; background: transparent; color: #9ca3af; cursor: pointer;
      font-size: 18px; line-height: 1; padding: 0 4px;
    }
    .mmt-multi-del:hover { color: #e53e3e; }
    .mmt-multi-add {
      align-self: flex-start; border: 1px dashed #b7c7e0; background: #f2f7fd;
      color: #2B5CB8; border-radius: 6px; padding: 5px 10px; font-size: 11px;
      font-weight: 700; cursor: pointer;
    }
    .mmt-multi-add:hover { background: #e6f0fb; }
    .mmt-priority-row { display: flex; gap: 4px; }
    .mmt-prio-btn {
      border: 2px solid #d8e4f0; border-radius: 5px;
      padding: 5px 10px; font-size: 10px; font-weight: 700;
      cursor: pointer; background: white; color: #6b7280;
    }
    .mmt-prio-btn--active { border-color: #2B5CB8; color: #2B5CB8; background: rgba(43,92,184,0.05); }

    /* Content / grid */
    .mmt-content { flex: 1; overflow-y: auto; padding: 20px 24px; }

    .mmt-loading, .mmt-error {
      display: flex; align-items: center; gap: 10px;
      padding: 24px; color: #8a9bb0; font-size: 13px;
    }
    .mmt-spinner {
      width: 20px; height: 20px; border: 2px solid #e0e8f4;
      border-top-color: #2B5CB8; border-radius: 50%;
      animation: mmt-spin 0.7s linear infinite;
    }
    @keyframes mmt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .mmt-error { color: #991b1b; }
    .mmt-error .material-icons { font-size: 18px; }

    .mmt-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }

    /* Template card */
    .mmt-card {
      border: 2px solid #d8e4f0; border-radius: 10px;
      overflow: hidden; cursor: pointer; transition: all 0.15s;
      background: white;
    }
    .mmt-card:hover { border-color: #2B5CB8; box-shadow: 0 4px 14px rgba(43,92,184,0.12); }
    .mmt-card--selected { border-color: #2B5CB8; }

    .mmt-preview {
      background: #f0f4fa; height: 130px;
      display: flex; flex-direction: column; overflow: hidden;
      position: relative; border-top: 3px solid #1B3A6B;
    }
    .mmt-preview-header {
      height: 24px; display: flex; align-items: center; padding: 0 10px;
    }
    .mmt-preview-header-line {
      height: 2px; background: rgba(255,255,255,0.5); border-radius: 2px; width: 60%;
    }
    .mmt-preview-lines { padding: 10px; }
    .mmt-pline { height: 4px; background: rgba(27,58,107,0.15); border-radius: 2px; margin-bottom: 5px; }
    .mmt-pline--title { height: 6px; background: rgba(27,58,107,0.3); width: 60%; }
    .mmt-pline--short { width: 40%; }

    .mmt-selected-badge {
      position: absolute; top: 6px; right: 6px;
      width: 22px; height: 22px; border-radius: 50%;
      background: #2B5CB8; color: white;
      display: flex; align-items: center; justify-content: center;
    }
    .mmt-selected-badge .material-icons { font-size: 13px; }
    .mmt-cat-badge {
      position: absolute; bottom: 6px; left: 8px;
      color: white; font-size: 9px; font-weight: 700;
      padding: 2px 6px; border-radius: 3px; text-transform: uppercase;
    }

    .mmt-card-info { padding: 10px 12px; }
    .mmt-card-name { font-size: 12px; font-weight: 700; color: #1B3A6B; }
    .mmt-card-sub { font-size: 10px; color: #8a9bb0; margin-top: 2px; }

    /* Upload custom card */
    .mmt-card--upload { border-style: dashed; }
    .mmt-preview--upload {
      flex-direction: column; align-items: center; justify-content: center;
      gap: 6px; background: #f8faff; border-top-color: #d8e4f0;
    }
    .mmt-preview--upload .material-icons { font-size: 32px; color: #aab5c5; }
    .mmt-upload-label { font-size: 11px; color: #8a9bb0; }

    /* Empty state */
    .mmt-empty {
      grid-column: 1 / -1; display: flex; flex-direction: column;
      align-items: center; padding: 40px; color: #aab5c5;
    }
    .mmt-empty .material-icons { font-size: 40px; margin-bottom: 8px; }
    .mmt-empty p { font-size: 13px; }

    /* Footer */
    .mmt-footer {
      background: white; border-top: 1px solid #e0e8f4;
      padding: 14px 24px; display: flex; align-items: center; gap: 12px;
    }
    .mmt-selected-label {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; color: #0D7A5F;
    }
    .mmt-selected-label .material-icons { font-size: 16px; }

    /* Buttons */
    .mmt-btn {
      display: inline-flex; align-items: center; gap: 6px;
      border-radius: 6px; padding: 9px 18px;
      font-size: 13px; font-weight: 600; cursor: pointer;
      border: none; transition: all 0.15s;
    }
    .mmt-btn .material-icons { font-size: 16px; }
    .mmt-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .mmt-btn--ghost {
      background: white; color: #6b7280; border: 1px solid #d8e4f0;
    }
    .mmt-btn--ghost:hover:not(:disabled) { background: #f0f4fa; }
    .mmt-btn--primary { background: #0D7A5F; color: white; }
    .mmt-btn--primary:hover:not(:disabled) { background: #0a6349; }
  `]
})
export class MailMemoTemplateComponent implements OnInit, OnDestroy {

  templates: MailTemplateDto[] = [];
  fileClasses: FileClassDto[] = [];
  selectedTemplate: MailTemplateDto | null = null;

  loading = false;
  loadError = '';
  opening = false;

  searchTerm = '';
  selectedCategory: TemplateCategory = 'ALL';

  // Metadata required before opening OO
  memoSubject = '';
  selectedFileClassId: number | '' = '';
  priority: 'NORMAL' | 'URGENT' | 'CONFIDENTIAL' = 'NORMAL';

  // Step 2 — configured memo fields (resolved from the document type)
  memoFields: MemoFieldDto[] = [];
  indexValues: Record<string, any> = {};
  /** Feature 2/7 — per-field ordered values for multiValue fields (e.g. Through). */
  multiValues: Record<string, string[]> = {};
  showFieldsStep = false;
  loadingFields = false;
  resolvedTypeKey = '';
  fieldsError = '';

  readonly priorities: Array<'NORMAL' | 'URGENT' | 'CONFIDENTIAL'> = ['NORMAL', 'URGENT', 'CONFIDENTIAL'];

  readonly categories: Array<{ value: TemplateCategory; label: string }> = [
    { value: 'ALL',     label: 'All' },
    { value: 'GENERAL', label: 'General' },
    { value: 'HR',      label: 'HR' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'LEGAL',   label: 'Legal' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private mailService: MailWorkflowService
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
    this.loadFileClasses();
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  // ── Data loading ─────────────────────────────────────────────────────────

  private loadTemplates(): void {
    this.loading = true;
    this.mailService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next:  (t) => { this.templates = t; this.loading = false; },
        error: (e) => { this.loadError = e?.error?.message ?? 'Could not load templates.'; this.loading = false; }
      });
  }

  private loadFileClasses(): void {
    this.mailService.getFileClasses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (fc) => this.fileClasses = fc });
  }

  // ── Filtering ────────────────────────────────────────────────────────────

  get filteredTemplates(): MailTemplateDto[] {
    return this.templates.filter(t => {
      const matchCat = this.selectedCategory === 'ALL' || t.category === this.selectedCategory;
      const matchSearch = !this.searchTerm ||
        t.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        t.description.toLowerCase().includes(this.searchTerm.toLowerCase());
      return matchCat && matchSearch;
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  selectTemplate(tpl: MailTemplateDto): void {
    this.selectedTemplate = tpl;
  }

  onCustomTemplate(event: Event): void {
    // Custom template upload — just navigate to initiate form with memo type
    // The actual upload is handled by the back end via the regular upload endpoint.
    // For now, treat it as selecting a blank template.
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    // Placeholder — in production, upload the custom .docx via uploadDocument()
    // and pass the resulting nodeId as templateId
    this.selectedTemplate = {
      id: '__custom__',
      name: file.name.replace('.docx', ''),
      category: 'GENERAL',
      description: 'Custom uploaded template'
    };
  }

  categoryColor(cat: string): string {
    return {
      GENERAL: '#1B3A6B',
      HR:      '#0D7A5F',
      LEGAL:   '#6B21A8',
      FINANCE: '#D46B08',
    }[cat] ?? '#1B3A6B';
  }

  openInEditor(): void {
    if (!this.selectedTemplate || !this.memoSubject || !this.selectedFileClassId) return;
    this.opening = true;

    // Feature 2/7 — fold ordered multi-value fields (e.g. Through) into
    // indexValues as a comma-separated string; the backend splits it, stores
    // it comma-separated on the aspect, and repeats the docx line per value.
    for (const f of this.memoFields) {
      if (this.isDirectoryField(f)) { continue; }   // person/role already structured in indexValues
      if (f.multiValue) {
        this.indexValues[f.propertyName] =
          (this.multiValues[f.propertyName] || []).map(s => (s || '').trim()).filter(Boolean).join(', ');
      }
    }

    this.mailService.createMemoFromTemplate(this.selectedTemplate.id, {
      subject: this.memoSubject,
      fileClassId: Number(this.selectedFileClassId),
      priority: this.priority,
      dateOfMail: new Date().toISOString().split('T')[0],
      documentTypeKey: this.resolvedTypeKey || this.selectedTemplate.documentTypeKey || undefined,
      indexValues: this.indexValues,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.opening = false;
          // Navigate to the OnlyOffice editor route with the node ID
          // The OO editor component is already wired in the existing app.
          // Pass draftId and nodeId as query params so OO callback can update the draft.
          this.router.navigate(['/mail-workflow/memo/edit', result.draftId], {
            queryParams: { nodeId: result.nodeId }
          });
        },
        error: (err) => {
          this.opening = false;
          this.loadError = err?.error?.message ?? 'Could not create memo document.';
        }
      });
  }

  /**
   * Step 1 → Step 2. Fetch the document type's memo-stage fields for the chosen
   * template. If there are none, skip the modal and create directly (verbatim).
   * If field resolution errors, fall back to direct creation so the flow is
   * never blocked by a config issue.
   */
  continueToFields(): void {
    if (!this.selectedTemplate || !this.memoSubject || !this.selectedFileClassId) { return; }
    this.fieldsError = '';
    this.loadingFields = true;
    this.mailService.getMemoFields(this.selectedTemplate.id, this.selectedTemplate.documentTypeKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loadingFields = false;
          this.resolvedTypeKey = res.documentTypeKey || '';
          this.memoFields = res.fields || [];
          // Pre-fill auto-date fields with today
          for (const f of this.memoFields) {
            if (f.autoPopulateDate && !this.indexValues[f.propertyName]) {
              this.indexValues[f.propertyName] = new Date().toISOString().split('T')[0];
            }
          }
          if (this.memoFields.length === 0) {
            this.openInEditor();          // nothing to capture — create directly
          } else {
            this.showFieldsStep = true;
          }
        },
        error: () => {
          this.loadingFields = false;
          this.openInEditor();            // never block on config issues
        }
      });
  }

  backToTemplates(): void {
    this.showFieldsStep = false;
  }

  /** True when every mandatory memo field has a value. */
  canCreateMemo(): boolean {
    for (const f of this.memoFields) {
      if (!f.mandatory) { continue; }
      if (this.isDirectoryField(f)) {
        const v = this.indexValues[f.propertyName];
        const ok = Array.isArray(v) ? v.length > 0 : !!v;
        if (!ok) { return false; }
      } else if (f.multiValue) {
        const has = (this.multiValues[f.propertyName] || []).some(v => (v || '').trim() !== '');
        if (!has) { return false; }
      } else {
        const v = this.indexValues[f.propertyName];
        if (v === undefined || v === null || String(v).trim() === '') { return false; }
      }
    }
    return true;
  }

  /** person / role fields use the Keycloak directory picker (structured value kept in indexValues). */
  isDirectoryField(f: MemoFieldDto): boolean {
    const t = (f.type || '').toLowerCase();
    return t === 'person' || t === 'role' || t === 'both' || t === 'personorrole';
  }
  dirMode(f: MemoFieldDto): 'person' | 'role' | 'both' {
    const t = (f.type || '').toLowerCase();
    if (t === 'role') { return 'role'; }
    if (t === 'both' || t === 'personorrole') { return 'both'; }
    return 'person';
  }

  // ── Feature 2/7 — multi-value (+Add) field helpers ──────────────────────
  multiRows(f: MemoFieldDto): string[] {
    const key = f.propertyName;
    if (!this.multiValues[key] || this.multiValues[key].length === 0) {
      this.multiValues[key] = [''];
    }
    return this.multiValues[key];
  }
  /** Track by position so typing (which mutates the string) doesn't recreate the input → keeps focus. */
  trackByMultiRow(i: number): number { return i; }
  setMultiRow(f: MemoFieldDto, i: number, v: string): void {
    this.multiRows(f)[i] = v;
  }
  addMultiRow(f: MemoFieldDto): void {
    this.multiRows(f).push('');
  }
  removeMultiRow(f: MemoFieldDto, i: number): void {
    const arr = this.multiRows(f);
    arr.splice(i, 1);
    if (arr.length === 0) { arr.push(''); }
  }

  goBack(): void {
    this.router.navigate(['/mail-workflow/new']);
  }
}